/**
 * Du déboursé au planning : durées, phases et barres.
 *
 * Ce module ne dessine rien et ne lit aucune date du jour — il traduit des heures
 * en demi-journées, et des phases en segments. Le calendrier viendra après ; ici
 * tout est en **jours relatifs**, J1 étant le premier jour du chantier.
 *
 * ## La règle qui gouverne ce fichier
 *
 * *PRD §4.4 : « heures MO ÷ 4 = durée en ½ journées ».*
 *
 * Deux précisions que la mesure a imposées, et qui ne sont pas dans cette phrase :
 *
 *  1. **Ce sont les heures de MAIN-D'ŒUVRE, pas toutes les heures.** Une pelle et
 *     son conducteur travaillent en même temps. Mesuré sur les chantiers réels :
 *     5 658 h de main-d'œuvre pour 8 187 h de matériel — se tromper de base
 *     donnerait des barres 2,4 fois trop longues, et aucun total ne bougerait.
 *  2. **L'arrondi est à la demi-journée SUPÉRIEURE, et il porte sur la TÂCHE.**
 *     Décision du métier : « si une tâche prend 3 h, en réalité c'est la
 *     demi-journée qui est cramée ». La maille — la tâche plutôt que chaque
 *     ressource — a été tranchée le 11/08/2026 : chiffré sur trois chantiers réels,
 *     arrondir par tâche coûte +170 h (+4 704 €), par ressource +1 022 h
 *     (+27 177 €). Le facteur six entre les deux vient de ce qu'on suppose : une
 *     seule personne par tâche, ou aucune mutualisation d'une demi-journée entre
 *     deux tâches.
 *
 * ## Ce que l'arrondi rend visible
 *
 * Il chiffre un **coût caché** que rien n'affichait : une tâche de 6 h de
 * main-d'œuvre occupe deux demi-journées, soit 8 h payées. Ces deux heures ne sont
 * dans aucun déboursé, et personne ne les voyait.
 */
import { dec, Decimal } from "./nombre.js";
import { estEnHeures, estEnHeuresDeMainDOeuvre, estMachineImmobilisee, STATUT_RETIRE, } from "./depo.js";
/**
 * Heures dans une demi-journée.
 *
 * PRD §4.4 : « journée de 8 h = 4 + 4 ». Hypothèse à valider avec le métier
 * (horaires 8h-12h / 13h-17h), et c'est pourquoi la valeur est déclarée ici plutôt
 * qu'écrite dans les calculs : le jour où le métier la change, un seul endroit.
 *
 * Elle deviendra un paramètre administrable quand la table `parametre` existera —
 * l'architecture §8.2 la prévoit déjà. En attendant, une constante nommée vaut mieux
 * qu'un `4` semé dans trois fichiers.
 */
export const HEURES_PAR_DEMI_JOURNEE = 4;
/** Deux demi-journées par jour : matin et après-midi. */
export const DEMI_JOURNEES_PAR_JOUR = 2;
/**
 * L'échelle à laquelle une heure a un sens : le centième, soit 36 secondes.
 *
 * Elle existe à cause d'un cas mesuré sur un déboursé réel. Une tâche y porte
 * `32,0000000000000019` h de main-d'œuvre : le résidu de la troncature du temps
 * unitaire par l'ERP, deux milliardièmes de milliardième d'heure. Un arrondi
 * supérieur appliqué tel quel en fait **une demi-journée entière de 4 h**, et la
 * tâche passe de 8 à 9 demi-journées.
 *
 * C'est le même piège que les 959 valeurs qui n'entraient pas dans leur colonne, et
 * la réponse est la même : **ramener à l'échelle de restitution avant de décider**.
 * Un dépassement réel — 32,01 h — franchit toujours le seuil ; un résidu de calcul,
 * non.
 */
const DECIMALES_HEURE = 2;
/**
 * La durée qu'une tâche occuperait, en demi-journées.
 *
 * Arrondi **au-dessus** : 3 h consomment une demi-journée entière, 6 h en consomment
 * deux. C'est la règle du métier, et elle dit quelque chose de vrai — on ne rend pas
 * une demi-journée entamée.
 *
 * Rend `0` quand la tâche ne porte aucune heure de main-d'œuvre : un poste de
 * fournitures seules ne s'étale pas dans le temps. **Zéro n'est pas une durée
 * proposée par défaut**, c'est l'absence de proposition — et l'écran doit le dire
 * ainsi plutôt que d'afficher une barre de rien.
 */
export function demiJourneesPour(heuresMO, personnes = 1) {
    const brutes0 = typeof heuresMO === "string" ? dec(heuresMO) : heuresMO;
    // **L'effectif divise les heures, il ne les multiplie pas.** 28,75 h à trois, ce
    // sont 9,58 h chacune : même travail, même déboursé, calendrier divisé. Un
    // effectif absent ou absurde vaut une personne — le comportement d'avant la
    // notion, pour qu'aucun chantier existant ne bouge.
    const parts = Number.isInteger(personnes) && personnes > 0 ? personnes : 1;
    const brutes = parts === 1 ? brutes0 : brutes0.div(parts);
    if (brutes.lte(0))
        return 0;
    // **Les heures sont ramenées au centième AVANT l'arrondi supérieur**, et ce n'est
    // pas une coquetterie : sur un déboursé réel, une tâche porte
    // 32,0000000000000019 h de main-d'œuvre — le résidu de la troncature du temps
    // unitaire par l'ERP. Sans cette réduction, ce 2×10⁻¹⁸ h devient une demi-journée
    // entière de 4 h, et la tâche passe de 8 à 9 demi-journées. Un résidu inférieur à
    // 36 secondes n'est pas du travail.
    const heures = brutes.toDecimalPlaces(DECIMALES_HEURE, Decimal.ROUND_HALF_UP);
    if (heures.lte(0))
        return 0;
    return heures.div(HEURES_PAR_DEMI_JOURNEE).ceil().toNumber();
}
/**
 * La durée qu'une tâche occupe d'après son DÉTAIL : la plus longue de ses lignes.
 *
 * *Décision du métier du 12/08/2026 : « les équipes ne peuvent pas être affectées sur
 * le titre 1.1.2, il faut affecter les équipes sur le détail MOICDC et MOIOUVTRX […]
 * du coup le sous-titre aura une barre qui correspond à la barre la plus longue du
 * détail ».*
 *
 * **Le maximum, et non la somme.** Additionner les heures de main-d'œuvre supposait
 * que les gens travaillent l'un APRÈS l'autre : sur un poste réel, 4 h de chef et 8 h
 * d'ouvrier donnaient 12 h, soit 3 demi-journées, alors que les deux sont là en même
 * temps et que la tâche dure ce que dure le plus long — 2.
 *
 * Chaque ligne divise ses PROPRES heures par son PROPRE nombre d'unités. Le périmètre
 * est celui de `heuresMO` — main-d'œuvre qui porte la durée —, par le même prédicat
 * et non par une copie : le transport reste dehors, ses heures étant concomitantes au
 * travail et non consécutives.
 *
 * **Une seule définition, appelée partout** : par la vue transmise à l'écran, par le
 * recalage de barre du serveur et par la proposition de planning. Trois calculs de la
 * même durée finiraient par en donner trois, et personne ne saurait lequel fait foi.
 */
export function demiJourneesDuDetail(ouvrage) {
    let plusLongue = 0;
    for (const r of ouvrage.ressources) {
        if (r.statut === STATUT_RETIRE)
            continue;
        if (!estEnHeuresDeMainDOeuvre(r) || !r.porteDuree)
            continue;
        const duree = demiJourneesDeLaLigne(r);
        if (duree > plusLongue)
            plusLongue = duree;
    }
    return plusLongue;
}
/**
 * La durée qu'une LIGNE implique : ses heures divisées par son nombre d'unités.
 *
 * C'est la grandeur que la migration 012 rend saisissable : depuis que chaque ligne
 * porte ses barres, c'est cette durée-là que l'écran propose et que l'étirement fait
 * bouger. Deux ouvriers sur 8 h, c'est une demi-journée ; un chef sur 4 h aussi.
 *
 * **Zéro pour ce qui n'est pas en heures**, et ce n'est pas une durée nulle : une tonne
 * de grave ne s'étale pas dans le temps, elle se livre. Ces lignes ne reçoivent aucune
 * barre de durée — leurs livraisons sont un autre objet.
 *
 * Une ligne retirée du chiffrage ne dure rien non plus : elle ne coûte plus et
 * n'occupe personne, comme un poste écarté ne se planifie pas.
 */
export function demiJourneesDeLaLigne(ressource) {
    if (ressource.statut === STATUT_RETIRE)
        return 0;
    if (!estEnHeures(ressource.unite))
        return 0;
    return demiJourneesPour(ressource.quantiteReelle, ressource.unites ?? 1);
}
/**
 * Les heures réellement immobilisées par une durée en demi-journées.
 *
 * C'est `durée × 4`, et l'écart avec les heures chiffrées est le **coût caché** de
 * l'arrondi. Sur une tâche de 6 h : 8 h immobilisées, 2 h que personne ne voyait.
 */
export function heuresImmobilisees(demiJournees, unites = 1) {
    // **Chaque unité présente immobilise la durée entière.** Trois personnes sur trois
    // demi-journées, ce sont 36 h de présence, pas 12. Oublier le facteur ferait
    // afficher un GAIN de −16,75 h là où il y a un surcoût de +7,25 h — l'écran
    // annoncerait l'inverse de la vérité.
    const compte = Number.isInteger(unites) && unites > 0 ? unites : 1;
    return dec(demiJournees).times(HEURES_PAR_DEMI_JOURNEE).times(compte);
}
/**
 * Combien d'unités il faut pour tenir ces heures dans cette durée.
 *
 * L'inverse de `demiJourneesPour` : la durée commande, et les nombres en découlent.
 * Une niveleuse chiffrée 28,75 h dans une tâche de 4 demi-journées — donc 16 h de
 * calendrier — en demande deux ; la benne d'approvisionnement, chiffrée 55,25 h, en
 * demande quatre. **C'est ce calcul qui dispense de classer les codes matériel** :
 * chaque ligne annonce son propre besoin, et le conducteur ne remplit que celles
 * que son équipe conduit.
 *
 * Rend `0` quand il n'y a pas d'heures : une ligne de fournitures n'immobilise
 * aucune machine, et proposer « 1 » inventerait un engin.
 */
export function unitesPour(heures, demiJournees) {
    const brutes = typeof heures === "string" ? dec(heures) : heures;
    if (brutes.lte(0) || demiJournees <= 0)
        return 0;
    const heuresDisponibles = dec(demiJournees).times(HEURES_PAR_DEMI_JOURNEE);
    // Même réduction au centième que `demiJourneesPour`, et pour la même raison : un
    // résidu de troncature de l'ERP ne doit pas réclamer une machine de plus.
    const besoin = brutes.div(heuresDisponibles).toDecimalPlaces(DECIMALES_HEURE, Decimal.ROUND_HALF_UP);
    return besoin.lte(0) ? 0 : besoin.ceil().toNumber();
}
/**
 * Les effectifs qui ne coûtent RIEN en arrondi — les diviseurs de la durée à une personne.
 *
 * Ce n'est pas une heuristique, ça se démontre. Soit `D` la durée à une personne,
 * donc `D - 1 < heures / 4 ≤ D`. Si `n` divise `D`, alors `heures / (4n)` tombe dans
 * `(D/n - 1, D/n]` dont la borne haute est entière : la durée vaut exactement `D/n`,
 * et la présence `n × (D/n) × 4 = 4D` — **la même quel que soit `n`**. Tout effectif
 * qui divise `D` gaspille donc autant qu'une personne seule ; tout autre gaspille
 * strictement plus.
 *
 * *Vérifié sur 28,75 h (D = 8) : 1, 2, 4 et 8 personnes coûtent toutes +3,25 h,
 * tandis que 3 en coûtent +7,25 h, 5 +11,25 h et 6 +19,25 h. Mettre 3 personnes est
 * donc toujours perdant — plus long que 4 ET plus coûteux. Un conducteur ne peut pas
 * le devinner ; l'écran peut le lui dire.*
 */
export function effectifsSansGaspillage(dureeAUnePersonne) {
    if (!Number.isInteger(dureeAUnePersonne) || dureeAUnePersonne <= 0)
        return [];
    const diviseurs = [];
    for (let n = 1; n <= dureeAUnePersonne; n++) {
        if (dureeAUnePersonne % n === 0)
            diviseurs.push(n);
    }
    return diviseurs;
}
/**
 * Les barres d'une tâche, triées selon qui les porte.
 *
 * Une seule lecture de la liste plutôt qu'un filtre à chaque endroit qui en a besoin :
 * la durée de la tâche, celle de chaque ligne et le coût de l'arrondi posent tous la
 * même question, et trois filtres jumeaux finiraient par différer d'une condition.
 */
export function repartirLesBarres(phases) {
    const propres = [];
    const parRang = new Map();
    for (const phase of phases) {
        if (phase.ligneRang === null) {
            propres.push(phase);
            continue;
        }
        const liste = parRang.get(phase.ligneRang) ?? [];
        liste.push(phase);
        parRang.set(phase.ligneRang, liste);
    }
    return { propres, parRang };
}
/**
 * L'étendue d'une phase : son premier et son dernier jour relatif.
 *
 * Une phase de 3 demi-journées commençant l'après-midi du jour 4 occupe l'après-midi
 * du 4, puis la matinée et l'après-midi du 5 : elle finit donc le jour 5. Ne pas
 * tenir compte de la position décalerait la fin d'un jour sur toutes les durées
 * impaires — et un Gantt faux d'un jour sur la moitié de ses barres ne se remarque
 * pas tout de suite.
 */
export function finDePhase(phase) {
    const decalage = phase.position === "apres_midi" ? 1 : 0;
    const demiJourneesOccupees = decalage + phase.demiJournees;
    // On compte les jours ENTAMÉS : 1 demi-journée entame un jour, 3 en entament deux.
    const jours = Math.ceil(demiJourneesOccupees / DEMI_JOURNEES_PAR_JOUR);
    return phase.debutRelatif + jours - 1;
}
/* ---------------------------------------------------------------------------
 * Jour + position ⇄ index de demi-journée
 * ------------------------------------------------------------------------- */
/**
 * La position d'une barre en une seule grandeur : son rang de demi-journée, à partir de 0.
 *
 * J1 matin vaut `0`, J1 après-midi `1`, J2 matin `2`. Le modèle stocke la position en
 * DEUX morceaux — un jour et un moment — parce que c'est ainsi que le métier la
 * nomme ; mais tout ce qui *déplace* une barre raisonne en demi-journées, et mélanger
 * les deux unités est une faute silencieuse.
 *
 * *Défaut mesuré le 12/08/2026, signalé par le métier : « il faut pouvoir déplacer les
 * barres par demi-journée, là je vois que tu le fais par jour ». Le glissé mesurait
 * bien un déplacement en demi-journées, puis l'ajoutait à `debutRelatif` qui est un
 * JOUR — la barre partait donc deux fois plus loin que le curseur, et les positions
 * d'après-midi étaient inatteignables à la souris.*
 */
export function indexDemiJournee(position) {
    return (position.debutRelatif - 1) * DEMI_JOURNEES_PAR_JOUR + (position.position === "apres_midi" ? 1 : 0);
}
/**
 * L'inverse : d'un rang de demi-journée au jour et au moment qui le désignent.
 *
 * Borné à zéro — une barre ne commence pas avant le premier jour du chantier. Le
 * plancher porte sur l'INDEX et non sur le jour : borner le jour laisserait passer
 * un après-midi négatif, qui se lirait comme une barre du jour 1 sans que rien ne le
 * signale.
 */
export function depuisIndexDemiJournee(index) {
    const borne = Math.max(0, Math.trunc(index));
    return {
        debutRelatif: Math.floor(borne / DEMI_JOURNEES_PAR_JOUR) + 1,
        position: borne % DEMI_JOURNEES_PAR_JOUR === 0 ? "matin" : "apres_midi",
    };
}
/**
 * Coupe le travail d'une ligne en une reprise de plus, **en parts égales**.
 *
 * *Demande de Chris, 24/09/2026 : « si je clique une fois cela veut dire que je découpe la
 * tâche en deux, donc il répartit les heures en deux parts égales ; si je reclique il
 * divise en trois, puis en quatre ».*
 *
 * **La durée totale ne change pas**, et c'est ce qui fait qu'aucun euro ne bouge : les
 * heures d'une ligne ne se recalculent que quand la durée totale de ses barres change
 * (`modifierPhase`). Le bouton posait jusqu'ici une demi-journée DE PLUS, si bien que la
 * ligne affichait plus de demi-journées posées que ses heures n'en impliquent.
 *
 * Trois règles :
 *
 *  - **le reste d'une division inexacte va aux premières reprises** — 5 demi-journées en
 *    deux font 3 puis 2 : la demi-journée est le grain du planning, elle ne se coupe pas ;
 *  - **chaque barre garde son départ**, parce qu'il a pu être placé à la main (J105, puis
 *    J114). Une barre qui grandit et viendrait mordre sur la suivante repousse celle-ci
 *    juste derrière elle, jamais plus loin. **Sauf une barre COLLÉE à la précédente** : son
 *    départ n'a été posé par personne, il est celui qu'un hachage précédent lui a donné. Elle
 *    suit donc la précédente quand celle-ci rétrécit — sans quoi 4 + 4 coupé en trois
 *    rendait 3, un trou, 3 puis 2 (constaté à l'écran le 28/09/2026) ;
 *  - **la nouvelle reprise se pose collée à la dernière** : couper une barre ne recule pas
 *    la fin de la ligne. On la déplace ensuite là où elle doit être.
 *
 * `null` quand il n'y a pas assez de demi-journées pour une part de plus : une ligne de
 * 2 demi-journées se coupe en deux, pas en trois. L'appelant le dit plutôt que de
 * fabriquer des barres de zéro.
 *
 * ## Quand la ligne porte des heures : des parts égales EN HEURES
 *
 * *Chris, 28/09/2026, sur la 1.5 d'APO260098 : « au 4e hachage il ne divise pas sur l'ensemble
 * des 3 premières barres mais juste sur la dernière ; je veux que la répartition des heures soit
 * faite sur toutes les barres quel que soit le nombre de hachages ».* Six demi-journées en quatre
 * rendaient 2-2-1-1 : à la demi-journée près, c'était le plus égal possible, et pourtant les trois
 * premières barres n'avaient pas bougé.
 *
 * Choix de Chris entre deux règles : **chaque barre reçoit la même part d'heures et s'arrondit à
 * la demi-journée supérieure** — 24 h en quatre, ce sont quatre barres de 2 demi-journées portant
 * 6 h chacune. Les heures et le coût ne bougent pas (le hachage n'appelle jamais l'étirement) ;
 * le planning, lui, occupe 8 demi-journées au lieu de 6. Le plan de charge suit : chaque barre y
 * porte sa part (`heuresChiffreesParJour`).
 *
 * La taille ne descend jamais sous `total ÷ parts` : une ligne posée plus longue que ses heures
 * ne se raccourcit pas en silence parce qu'on l'a coupée. Et il n'y a plus de refus : une part
 * vaut au moins une demi-journée, quel que soit le nombre de clics.
 */
export function hacherEnPartsEgales(barres, chiffrage) {
    if (barres.length === 0)
        return null;
    const total = barres.reduce((somme, b) => somme + b.demiJournees, 0);
    const parts = barres.length + 1;
    const heures = chiffrage === undefined ? dec(0) : dec(chiffrage.heures);
    let taille;
    if (chiffrage !== undefined && heures.greaterThan(0)) {
        const parPart = Math.max(1, demiJourneesPour(heures.div(parts), chiffrage.unites ?? 1), Math.floor(total / parts));
        taille = () => parPart;
    }
    else {
        if (parts > total)
            return null;
        const base = Math.floor(total / parts);
        const reste = total - base * parts;
        taille = (rang) => base + (rang < reste ? 1 : 0);
    }
    // L'ordre du TEMPS, et non celui de création : une reprise posée après coup peut
    // précéder les autres sur la frise.
    const triees = [...barres].sort((a, b) => indexDemiJournee(a) - indexDemiJournee(b));
    const reecrites = [];
    let curseur = 0;
    // La fin AVANT le geste de la barre précédente : c'est elle qui dit si la barre était collée.
    let finPrecedente = null;
    triees.forEach((b, rang) => {
        const origine = indexDemiJournee(b);
        const collee = finPrecedente !== null && origine === finPrecedente + 1;
        const depart = collee ? curseur : Math.max(origine, curseur);
        const demiJournees = taille(rang);
        reecrites.push({ barre: b.barre, demiJournees, ...depuisIndexDemiJournee(depart) });
        curseur = depart + demiJournees;
        finPrecedente = Math.max(finPrecedente ?? origine - 1, origine + b.demiJournees - 1);
    });
    return {
        reecrites,
        nouvelle: { demiJournees: taille(triees.length), ...depuisIndexDemiJournee(curseur) },
    };
}
/**
 * Ce que chaque jour relatif porte, pour un jeu de barres.
 *
 * *Deux lectures d'un seul parcours, et c'est délibéré.* Le **compte** est la grandeur du
 * plan de charge (PRD §4.7 : « le besoin en heures par type de ressource générique, par
 * jour […] base 4 h par ½ journée »). Les **positions** disent quel moment est pris —
 * c'est ce que le signalement de conflit doit nommer : *« ressource déjà utilisée le
 * matin sur le chantier X »* (Chris, 15/08/2026). Les calculer séparément ferait deux
 * parcours d'une même donnée, qui finiraient par ne plus s'accorder.
 *
 * Une barre n'est pas une durée abstraite : elle occupe des demi-journées **précises**,
 * et une barre de trois demi-journées commencée un après-midi occupe un jour à moitié
 * et le suivant en entier. Compter `demiJournees / 2` par jour donnerait 1,5 jour sur
 * le premier et rien sur le second — la charge tomberait le mauvais jour, et un plan de
 * charge qui se trompe de jour ne sert à rien.
 *
 * Le parcours passe donc par l'**index de demi-journée**, la seule unité dans laquelle
 * une barre se déplace (voir `indexDemiJournee`). Mélanger jours et demi-journées est la
 * faute que ce produit a déjà payée une fois.
 *
 * Les chevauchements **s'additionnent dans le compte** et **ne se répètent pas dans les
 * positions** : deux reprises posées le même matin occupent bien deux demi-journées de
 * charge, mais un seul matin. Confondre les deux ferait annoncer « matin et matin ».
 */
export function occupationParJour(phases) {
    const parJour = new Map();
    for (const phase of phases) {
        if (!Number.isInteger(phase.demiJournees) || phase.demiJournees <= 0)
            continue;
        const depart = indexDemiJournee(phase);
        for (let pas = 0; pas < phase.demiJournees; pas += 1) {
            const { debutRelatif: jour, position } = depuisIndexDemiJournee(depart + pas);
            const courant = parJour.get(jour) ?? { demiJournees: 0, positions: new Set() };
            courant.demiJournees += 1;
            courant.positions.add(position);
            parJour.set(jour, courant);
        }
    }
    const rendu = new Map();
    for (const [jour, occupation] of parJour) {
        rendu.set(jour, {
            demiJournees: occupation.demiJournees,
            // L'ordre est celui de la journée, pas celui de l'insertion : « après-midi et
            // matin » se lit comme une faute, et l'ordre d'insertion dépend de l'ordre des
            // barres, que rien ne garantit.
            positions: ["matin", "apres_midi"].filter((p) => occupation.positions.has(p)),
        });
    }
    return rendu;
}
/**
 * Les heures CHIFFRÉES d'une ligne, réparties sur les demi-journées qu'elle occupe.
 *
 * *Décision de Chris du 04/09/2026, qui revient sur la règle d'origine : « il faut revenir sur
 * une règle — quand il y a 2 h de compté le matin il faut prendre en compte 2 heures posées,
 * sinon cela pose un souci ; idem si c'est pour un après-midi ; si on parle de 1 h, 2 h ou
 * 3 h, il ne faut pas mettre 4 dans heures posées. »*
 *
 * ## Ce que le plan de charge comptait avant, et pourquoi cela change
 *
 * Il comptait des **présences** : `demi-journées × 4 h × exemplaires`, quel que soit le
 * chiffrage. Une ligne de 2 h posée sur une matinée pesait 4 h, au motif que la personne est
 * bien immobilisée une demi-journée. C'était défendable, et cela n'a pas tenu à l'usage : le
 * responsable lit ce tableau pour savoir **quel travail est engagé** un jour donné, et un
 * chiffre qui ne se retrouve nulle part dans la contre-étude ne se vérifie pas.
 *
 * L'écart entre les deux grandeurs n'est pas perdu pour autant : c'est le **coût caché de
 * l'arrondi** (`heuresImmobilisees`), qui vit à l'écran du déboursé et le dit en euros.
 *
 * ## Le remplissage, et non le lissage
 *
 * *Choix de Chris entre les trois formes possibles :* chaque demi-journée est pleine — 4 h par
 * exemplaire — et **la dernière porte le reste**. Six heures sur deux demi-journées font 4 h
 * puis 2 h, et non 3 h puis 3 h. C'est ce qui se passe sur le chantier : on travaille la
 * matinée entière et l'on finit le lendemain. Lisser aurait fait qu'aucune demi-journée ne
 * paraisse jamais pleine, et le plan de charge n'aurait plus jamais montré une journée saturée.
 *
 * **Depuis le 28/09/2026, le remplissage se fait barre par barre** : chaque barre reçoit sa part
 * des heures au prorata de sa durée, et c'est à l'intérieur de la barre que la dernière
 * demi-journée porte le reste (voir le corps de la fonction). Une ligne d'une seule barre n'y voit
 * aucune différence.
 *
 * L'ordre du remplissage est **chronologique**, par index de demi-journée. C'est la seule
 * grandeur dans laquelle une barre se déplace ; remplir dans l'ordre des barres dépendrait de
 * l'ordre d'insertion, que rien ne garantit, et les heures sauteraient de jour en changeant
 * une barre sans y toucher.
 *
 * ## Un chevauchement ne crée pas de capacité
 *
 * Deux barres posées sur la même matinée occupent **une** matinée : la capacité d'une
 * demi-journée vaut 4 h par exemplaire, une fois. `occupationParJour` les additionne, parce
 * que c'est ce qu'il lui faut pour compter des demi-journées de charge ; ici on répartit un
 * chiffrage, et le doubler sur un même matin aurait annoncé 8 h de travail dans une matinée.
 *
 * ## Les heures qui ne tiennent pas sont RENDUES, jamais rognées
 *
 * Dix heures sur deux demi-journées ne rentrent pas : 4 h + 4 h, et 2 h restent. Elles
 * ressortent dans `nonPlacees` pour que l'écran les dise. Les faire disparaître livrerait un
 * plan de charge plus léger que le chiffrage sans que rien ne l'explique — et c'est exactement
 * la sorte d'écart que ce produit refuse de taire.
 */
export function heuresChiffreesParJour(phases, chiffrees, exemplaires = 1) {
    const compte = Number.isInteger(exemplaires) && exemplaires > 0 ? exemplaires : 1;
    const capacite = dec(HEURES_PAR_DEMI_JOURNEE).times(compte);
    const valides = phases
        .filter((p) => Number.isInteger(p.demiJournees) && p.demiJournees > 0)
        .map((p) => ({ depart: indexDemiJournee(p), demiJournees: p.demiJournees }))
        .sort((a, b) => a.depart - b.depart);
    // Les demi-journées occupées, sans doublon : un chevauchement n'ouvre pas une seconde
    // moitié de journée.
    const occupees = new Set();
    for (const b of valides) {
        for (let pas = 0; pas < b.demiJournees; pas += 1)
            occupees.add(b.depart + pas);
    }
    const ordonnees = [...occupees].sort((a, b) => a - b);
    let total = dec(chiffrees);
    if (total.isNegative())
        total = dec(0);
    /*
      **Chaque barre porte sa part**, au prorata de sa durée, et se remplit elle-même — Chris,
      28/09/2026, avec le hachage en parts égales d'heures : 24 h en quatre barres de 2
      demi-journées font 6 h par barre, soit 4 h puis 2 h, quatre fois. Remplir toute la ligne
      dans l'ordre du temps aurait chargé les trois premières barres à plein et laissé la dernière
      vide. Une ligne d'une seule barre se remplit exactement comme avant.
  
      Au centième d'heure, la dernière barre prenant l'arrondi : le total reste exact. Ce qu'une
      barre ne peut pas tenir (une demi-journée déjà prise par un chevauchement) repart ensuite
      dans l'ordre du temps sur la place libre de la ligne, puis ressort dans `nonPlacees`.
    */
    const placees = new Map();
    const libre = (index) => capacite.minus(placees.get(index) ?? dec(0));
    const poser = (index, voulu) => {
        const dispo = libre(index);
        const part = voulu.greaterThan(dispo) ? dispo : voulu;
        placees.set(index, (placees.get(index) ?? dec(0)).plus(part));
        return voulu.minus(part);
    };
    const duree = valides.reduce((s, b) => s + b.demiJournees, 0);
    let distribue = dec(0);
    let reste = dec(0);
    valides.forEach((b, rang) => {
        const part = rang === valides.length - 1
            ? total.minus(distribue)
            : total.times(b.demiJournees).div(duree).toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
        distribue = distribue.plus(part);
        let aPoser = part;
        for (let pas = 0; pas < b.demiJournees && aPoser.greaterThan(0); pas += 1) {
            aPoser = poser(b.depart + pas, aPoser);
        }
        reste = reste.plus(aPoser);
    });
    if (valides.length === 0)
        reste = total;
    for (const index of ordonnees) {
        if (!reste.greaterThan(0))
            break;
        reste = poser(index, reste);
    }
    const parJour = new Map();
    for (const index of ordonnees) {
        const part = placees.get(index) ?? dec(0);
        const { debutRelatif: jour, position } = depuisIndexDemiJournee(index);
        const courant = parJour.get(jour) ?? { heures: dec(0), moments: [] };
        courant.heures = courant.heures.plus(part);
        courant.moments.push({ position, heures: part.toFixed() });
        parJour.set(jour, courant);
    }
    const rendu = new Map();
    for (const [jour, valeur] of parJour) {
        rendu.set(jour, {
            heures: valeur.heures.toFixed(),
            // L'ordre est celui de la journée : l'index croissant le donne déjà, le matin venant
            // avant l'après-midi du même jour.
            moments: valeur.moments,
        });
    }
    return { parJour: rendu, nonPlacees: reste.toFixed() };
}
/**
 * Combien de demi-journées chaque jour relatif porte — la seule grandeur du plan de charge.
 *
 * Dérivée d'`occupationParJour` et non recalculée : une seconde traversée finirait par
 * compter autrement, et le plan de charge cesserait d'être d'accord avec le signalement
 * affiché sur la même ligne.
 */
export function demiJourneesParJour(phases) {
    const parJour = new Map();
    for (const [jour, occupation] of occupationParJour(phases)) {
        parJour.set(jour, occupation.demiJournees);
    }
    return parJour;
}
/**
 * Ce qu'un ensemble de phases occupe.
 *
 * **La durée et l'étendue sont deux grandeurs différentes**, et les confondre est
 * l'erreur que le hachage rend inévitable. Sur le classeur du métier, une tâche de
 * 8 demi-journées de travail — soit 4 jours — s'étale du jour 1 au jour 21 : son
 * étendue est de 21 jours.
 *
 * La durée alimente le plan de charge, l'étendue dit quand le chantier est occupé
 * par cette tâche. La première se somme, la seconde ne se somme jamais.
 */
export function dureeDesPhases(phases) {
    if (phases.length === 0) {
        return { demiJournees: 0, debut: null, fin: null, etendueJours: 0 };
    }
    let demiJournees = 0;
    let debut = Number.POSITIVE_INFINITY;
    let fin = 0;
    for (const phase of phases) {
        demiJournees += phase.demiJournees;
        if (phase.debutRelatif < debut)
            debut = phase.debutRelatif;
        const finPhase = finDePhase(phase);
        if (finPhase > fin)
            fin = finPhase;
    }
    return { demiJournees, debut, fin, etendueJours: fin - debut + 1 };
}
/**
 * La même enveloppe que `dureeDesPhases`, mais à la demi-journée près.
 *
 * *Demande du métier, 16/08/2026 : « les barres sur les grands chapitres ne se
 * déplacent que de journée en journée, il faut qu'on puisse les aligner sur les
 * demi-journées ».*
 *
 * `dureeDesPhases` rend des JOURS — c'est ce qu'il lui faut, elle sert au plan de
 * charge et aux libellés « J3 → J7 ». Mais une barre récapitulative dessinée sur ces
 * bornes commence forcément au matin et finit forcément au soir : un détail qui
 * travaille l'après-midi de J3 se résumait par une barre partant du matin de J3.
 * L'écart valait une demi-journée à chaque bout, et il ne se refermait jamais —
 * déplacer le détail d'une demi-journée ne bougeait pas le résumé.
 *
 * Rien ici ne remplace `dureeDesPhases` : les deux lectures cohabitent, l'une pour
 * nommer les jours, l'autre pour tracer.
 */
export function etendueEnDemiJournees(phases) {
    if (phases.length === 0)
        return null;
    let debut = Number.POSITIVE_INFINITY;
    let fin = Number.NEGATIVE_INFINITY;
    for (const phase of phases) {
        const depart = indexDemiJournee(phase);
        // `- 1` parce que la borne est INCLUSE : une barre d'une demi-journée posée au
        // matin de J1 occupe l'index 0, pas les index 0 et 1. Sans ce retrait, toute
        // barre déborderait d'une demi-journée sur sa droite — précisément le défaut
        // que cette fonction corrige.
        const arrivee = depart + phase.demiJournees - 1;
        if (depart < debut)
            debut = depart;
        if (arrivee > fin)
            fin = arrivee;
    }
    return { debut, fin };
}
/**
 * Ce qu'une TÂCHE occupe, une fois que ce sont ses lignes qui portent les barres.
 *
 * *Vision du métier du 12/08/2026 : « la barre du titre devient un résumé — la plus
 * longue de son détail, ni déplaçable ni étirable ».*
 *
 * **Le maximum des lignes, et non la somme de toutes les barres.** C'est la même
 * correction que celle de la migration 011 sur les heures : additionner supposerait que
 * les ressources se succèdent, alors qu'elles sont sur le chantier en même temps. Sur
 * un poste réel, un chef sur 1 demi-journée et un ouvrier sur 2 donnent une tâche de 2,
 * pas de 3.
 *
 * Chaque ligne, en revanche, **somme** ses propres barres : c'est le hachage, et une
 * ligne reprise en trois fois travaille bien trois fois.
 *
 * L'étendue — début, fin — reste l'enveloppe de TOUT ce qui est posé, lignes comprises.
 * Elle répond à l'autre question : non pas « combien de travail » mais « de quand à
 * quand le chantier est occupé par cette tâche ». Les confondre fausserait l'une ou
 * l'autre, et c'est le hachage qui rend la distinction inévitable.
 */
export function dureeDeLaTache(phases) {
    const enveloppe = dureeDesPhases(phases);
    const { propres, parRang } = repartirLesBarres(phases);
    // Une tâche sans ligne en heures porte ses barres elle-même : sa durée est alors la
    // somme des siennes, comme avant la migration 012.
    if (parRang.size === 0)
        return { ...enveloppe, demiJournees: dureeDesPhases(propres).demiJournees };
    let plusLongue = 0;
    for (const barresDeLaLigne of parRang.values()) {
        const duree = dureeDesPhases(barresDeLaLigne).demiJournees;
        if (duree > plusLongue)
            plusLongue = duree;
    }
    return { ...enveloppe, demiJournees: plusLongue };
}
/**
 * La durée sur laquelle une ligne est comptée : celle qu'elle POSE, sinon celle qu'elle implique.
 *
 * Une ligne planifiée est comptée sur ce qu'elle occupe — c'est la seule façon qu'un
 * total soit la somme de ce que l'écran affiche, et un total qui contredit ses lignes ne
 * se défend pas. Une ligne sans barre retombe sur ses heures : sinon un chantier qu'on
 * vient d'importer annoncerait « aucun coût caché » alors que ses heures en portent déjà
 * un, et c'est ce chiffre-là qui a été mesuré avant qu'aucune barre n'existe.
 *
 * Une seule définition, appelée par le total du chantier et par la vue de chaque ligne :
 * deux calculs finiraient par diverger, et l'écran afficherait une ligne que le total ne
 * compte pas.
 */
export function demiJourneesRetenues(ressource, barres) {
    const posees = barres === undefined ? 0 : dureeDesPhases(barres).demiJournees;
    return posees > 0 ? posees : demiJourneesDeLaLigne(ressource);
}
/**
 * Les livraisons d'une tâche, par rang de ligne.
 *
 * Le pendant de `repartirLesBarres`, et pour la même raison : la lecture les rend par
 * POSTE — c'est toujours ce qu'on cherche —, et c'est ici qu'elles se rattachent à leur
 * ligne. Un second regroupement chez l'appelant finirait par ne pas ramasser les mêmes.
 */
export function repartirLesLivraisons(livraisons) {
    const parRang = new Map();
    for (const l of livraisons) {
        const liste = parRang.get(l.ligneRang) ?? [];
        liste.push(l);
        parRang.set(l.ligneRang, liste);
    }
    return parRang;
}
/**
 * Cette ligne se LIVRE-t-elle ?
 *
 * Ce qui n'est pas compté en heures : des tonnes, des mètres cubes, des unités, des mètres
 * linéaires. C'est précisément ce qu'un camion apporte.
 *
 * **Un seul prédicat pour deux questions**, et ce n'est pas un raccourci : « d'où ça
 * vient » (la provenance, migration 010) et « quand ça arrive » (les livraisons, 014)
 * portent sur exactement le même ensemble de lignes. Deux prédicats jumeaux finiraient par
 * différer d'une condition, et l'écran demanderait la carrière d'une ligne à laquelle il
 * refuserait une livraison.
 *
 * *Le critère est l'unité de la ligne, la seule information dont on dispose tant que les
 * articles n'ont pas de référentiel. C'est un choix d'affichage assumé, pas une règle du
 * métier — signalé comme tel le 12/08/2026, et non contesté.*
 */
export function porteDesLivraisons(ressource) {
    return ressource.statut !== STATUT_RETIRE && !estEnHeures(ressource.unite);
}
/** Ce qui est déjà placé dans le calendrier, tous jours confondus. */
export function quantiteLivree(livraisons) {
    let total = dec(0);
    for (const l of livraisons)
        total = total.plus(l.quantite);
    return total;
}
/**
 * Ce qu'il reste à placer : la quantité chiffrée moins ce qui est déjà livré.
 *
 * **L'invariant du métier est que la somme des livraisons égale la quantité de la ligne**,
 * et il s'AFFICHE au lieu de bloquer. C'est le même parti pris que partout ailleurs dans
 * ce produit — « ½ j posées / proposées », le coût caché, l'écart au devis : refuser une
 * livraison qui ne solde pas la ligne interdirait de commencer à répartir.
 *
 * Négatif = plus livré que chiffré. C'est un état légitime — le conducteur peut avoir
 * décidé d'un volume supérieur — mais il doit se voir, comme un écart d'arrondi négatif.
 */
export function resteALivrer(ressource, livraisons) {
    return ressource.quantiteReelle.minus(quantiteLivree(livraisons));
}
/**
 * L'échelle à laquelle une quantité livrée a un sens : le millionième.
 *
 * Celle de la colonne `livraison.quantite` et de `ouvrage.quantite_vente`. Répartir en
 * deux ne doit pas produire une valeur que la base tronquerait — sans quoi la somme des
 * deux moitiés cesserait d'égaler l'entier, et l'invariant se perdrait au premier hachage.
 */
const DECIMALES_QUANTITE = 6;
/**
 * Répartit une quantité en deux, **sans en perdre ni en inventer**.
 *
 * *« Le hachage répartit la quantité au lieu de découper une durée. »* C'est le geste qui
 * dit « 100 T mardi et 50 T jeudi » à partir de 150 T : une seule décision, et la somme
 * est préservée par construction — le conducteur n'a aucun calcul à refaire.
 *
 * La seconde part est le **reste**, jamais un second arrondi : sur 151 T, une moitié
 * arrondie deux fois donnerait 75,5 + 75,5 = 151 par chance, mais 0,000001 T donnerait
 * deux fois 0,000001 et créerait de la marchandise. Le reste ne peut pas mentir.
 *
 * Rend `null` quand la quantité est trop petite pour être coupée à cette échelle : mieux
 * vaut refuser le geste que produire une livraison de zéro, que la base refuserait avec un
 * message de contrainte incompréhensible.
 */
export function repartirLaQuantite(quantite) {
    if (quantite.lte(0))
        return null;
    const premiere = quantite.div(2).toDecimalPlaces(DECIMALES_QUANTITE, Decimal.ROUND_DOWN);
    const seconde = quantite.minus(premiere);
    if (premiere.lte(0) || seconde.lte(0))
        return null;
    return { premiere, seconde };
}
/**
 * Ce que les livraisons d'une ligne occupent : leur premier et leur dernier jour.
 *
 * Une livraison est un instant, donc il n'y a pas de durée à sommer — seulement une
 * étendue. C'est elle qui entre dans le résumé du titre et dans la frise : un chantier dont
 * la dernière livraison tombe à J30 est occupé jusqu'à J30, même si aucune barre de durée
 * n'y arrive.
 */
export function etendueDesLivraisons(livraisons) {
    let debut = null;
    let fin = null;
    for (const l of livraisons) {
        if (debut === null || l.jourRelatif < debut)
            debut = l.jourRelatif;
        if (fin === null || l.jourRelatif > fin)
            fin = l.jourRelatif;
    }
    return { debut, fin };
}
/**
 * Les transports d'une tâche, par rang de ligne.
 *
 * Le pendant de `repartirLesBarres` et de `repartirLesLivraisons`, pour la même raison :
 * la lecture les rend par POSTE, et un second regroupement chez l'appelant finirait par ne
 * pas ramasser les mêmes.
 */
export function repartirLesTransports(transports) {
    const parRang = new Map();
    for (const t of transports) {
        const liste = parRang.get(t.ligneRang) ?? [];
        liste.push(t);
        parRang.set(t.ligneRang, liste);
    }
    return parRang;
}
/**
 * Cette ligne se pose-t-elle en TRANSPORTS ?
 *
 * En heures, et déclarée transport au référentiel. Les deux conditions comptent : le
 * critère ne peut pas être le seul référentiel, parce qu'une ligne retirée du chiffrage
 * n'occupe plus rien — et il ne peut pas être la seule unité, puisque `VU`, `VUL` et `VP`
 * sont en heures sans être du transport.
 *
 * **Le troisième prédicat, et il est volontairement disjoint des deux autres.** La 014
 * disait « un seul prédicat pour deux questions » à propos de la provenance et des
 * livraisons, et c'était juste : elles portaient sur le même ensemble. Ici l'ensemble
 * diffère — une ligne de transport est en heures, donc elle ne se livre pas, et elle porte
 * pourtant une provenance. C'est `provenanceApplicable` qui réunit les deux, une fois.
 */
export function porteDesTransports(ressource) {
    return (ressource.statut !== STATUT_RETIRE && estEnHeures(ressource.unite) && ressource.estTransport);
}
/**
 * Le nombre d'unités retenu, quel qu'il soit : celui qu'on a, ou son repli.
 *
 * Écrit ici parce que la question se pose à six endroits — les heures engagées, le reste,
 * la proposition, le dépassement, le recalage du serveur, et le **plan général** — et que
 * six ternaires jumeaux finiraient par ne pas traiter le zéro de la même façon.
 *
 * *Exporté le 16/08/2026 : le plan général en avait écrit un septième, avec un repli à
 * une unité au lieu de celui de la ligne. Une rotation muette y comptait donc pour un
 * seul camion là où le déboursé en comptait quatre — et le tableau de bord annonçait le
 * quart des heures sans que rien ne le dise.*
 */
export function unitesRetenues(unites, repli = 1) {
    return Number.isInteger(unites) && (unites ?? 0) > 0 ? unites : repli;
}
/**
 * Les heures que les pastilles engagent réellement : `somme( heures × nombre de chacune )`.
 *
 * C'est la grandeur qui se compare aux heures chiffrées, et donc celle qui décide du
 * dépassement.
 *
 * **Le nombre est celui de la ROTATION depuis la 021**, et c'est ce qui rend l'exemple du
 * métier calculable : cinq camions 8 h le premier jour, deux 8 h le deuxième, trois 8 h le
 * dixième — 40 + 16 + 24 = 80 h. Multiplier une somme d'heures par un nombre unique aurait
 * donné 240 h ou 80 h ou 96 h selon le nombre choisi, et aucun des trois n'aurait rien
 * voulu dire.
 *
 * Une pastille sans nombre prend celui de la ligne ; à défaut, une unité — le comportement
 * d'avant la notion, pour qu'une ligne sans effectif déclaré ne disparaisse pas du calcul.
 *
 * **N'exige que les deux champs qu'elle lit**, et non une `Transport` entière : le serveur
 * calcule cette même grandeur sur des pastilles qu'il vient de lire en base, où reconstituer
 * les treize champs d'une rotation n'aurait servi qu'à satisfaire le type. Demander plus que
 * ce qu'on utilise pousse au cast, et un cast fait taire le compilateur au moment précis où
 * il aurait quelque chose à dire.
 */
export function heuresPosees(transports, unitesDeLaLigne = 1) {
    const defaut = unitesRetenues(unitesDeLaLigne);
    let total = dec(0);
    for (const t of transports)
        total = total.plus(t.heures.times(unitesRetenues(t.unites, defaut)));
    return total;
}
/**
 * Ce qu'il reste à engager sur une ligne, **en heures TOTALES**.
 *
 * *Métier, 13/08/2026 : « le total des heures devra correspondre à la contre-étude. »*
 * C'est cet invariant-là, et il se lit à la maille de la ligne : les rotations n'ont plus
 * toutes le même effectif, donc « ce qui reste par unité » ne veut plus rien dire — il
 * dépendrait de l'effectif de la prochaine, qu'on ne connaît pas encore.
 *
 * Négatif = plus engagé que chiffré. État légitime, et c'est exactement ce que la colonne H
 * doit montrer : le conducteur a décidé d'une rotation de plus.
 */
export function resteAPoser(ressource, transports) {
    return ressource.quantiteReelle.minus(heuresPosees(transports, ressource.unites));
}
/**
 * En dessous de quoi un écart d'heures n'est pas du travail : le demi-centième d'heure.
 *
 * *Défaut mesuré le 13/08/2026 sur `_MOICHAUF` à cinq unités.* La ligne porte
 * 55,24576271186440448 h ; la pastille, elle, est en `numeric(9,4)` — une durée saisie par
 * un conducteur, au quart d'heure près. Son cinquième arrondi vaut 11,0492, et
 * `11,0492 × 5 = 55,2460`. Le recalage y voyait un écart, multipliait le temps unitaire par
 * 1,0000000000000000001 — et **une pose qui ne devait rien coûter marquait la ligne
 * « quantité modifiée » face au devis figé**, pour dix-huit millionièmes de milliardième
 * d'heure.
 *
 * Le seuil est la moitié du centième — l'échelle à laquelle le produit restitue une heure
 * (36 secondes), déjà celle de `DECIMALES_HEURE`. Un écart réel — une pastille corrigée de
 * 8 h à 10 h — le franchit de très loin ; un résidu d'arrondi, jamais.
 *
 * **Une seule valeur, lue par le serveur et par l'écran** : celui-ci décide d'afficher le
 * dépassement, celui-là de déplacer des euros. Deux seuils différents feraient afficher un
 * écart que rien ne corrigerait, ou l'inverse.
 */
export const TOLERANCE_HEURE = "0.005";
/**
 * Ce qu'une pastille peut porter au maximum : **24 h**, la borne de la colonne en base.
 *
 * Ce n'est pas une convention de métier mais un fait : `transport.heures` porte un
 * `CHECK (heures > 0 AND heures <= 24)` depuis la 019, et un jour ne contient pas plus. Nommée
 * ici parce que le moteur doit proposer une valeur que la base accepte — sinon la pose échoue
 * sur un message de contrainte, qui ne veut rien dire pour un conducteur.
 *
 * *À ne pas confondre avec la journée du produit (4 h + 4 h). Celle-là bornait cette
 * proposition jusqu'au 21/08/2026 ; Chris l'a levée — il veut lire les heures chiffrées de la
 * ligne, pas un découpage en journées.*
 */
export const HEURES_MAXIMUM_PAR_PASTILLE = 24;
/**
 * Ce que la prochaine pastille propose : **le reste chiffré**, borné à ce que la base accepte.
 *
 * *Corrigé par Chris le 21/08/2026, en regardant une pastille posée automatiquement : « il
 * faudrait que les heures posées correspondent à la quantité du devis × les heures chiffrées ;
 * par exemple sur la ligne 04-03-10-00 il y a 11h08 de chiffré, il faut que tu mettes une
 * pastille avec 11h08 et non pas 8 h ».*
 *
 * **Le plafond de la journée était ma supposition, et elle est levée.** Je l'avais posé le
 * 13/08 en corrigeant un échec de contrainte, avec ce raisonnement : « 8 h, c'est ce qu'un
 * camion fait dans une journée, et une ligne de 27,62 h se place en 8 + 8 + 8 + 3,62, ce qu'un
 * conducteur écrirait à la main ». Le raisonnement se tient, mais personne ne l'avait demandé —
 * et il découpait en quatre gestes ce que le métier veut lire d'un coup : les heures chiffrées
 * de la ligne, sur une pastille.
 *
 * Reste le plafond **physique** de la colonne, `heures <= 24`, qui n'est pas une convention
 * mais la limite de ce que la base accepte : un jour ne contient pas plus. Au-delà, le reste ne
 * se perd pas — il demeure à poser, et la pastille suivante le proposera à son tour.
 *
 * *La division par le nombre d'unités, elle, ne bouge pas : c'est une règle du métier du
 * 13/08/2026 — « 40 h / 5 camions = 8 h ».*
 *
 * **Une seule définition, appelée des deux côtés** : le serveur pose cette valeur, l'écran
 * l'annonce sur son bouton. Deux calculs finiraient par proposer deux nombres différents,
 * et le bouton mentirait — c'est pourquoi ce changement porte AUSSI sur le bouton « + rotation »
 * et non seulement sur la pose automatique.
 */
export function heuresProposeesAPoser(ressource, transports, 
/** L'effectif de la rotation qu'on s'apprête à poser. Absent = celui de la ligne. */
unitesDeLaRotation = null) {
    const reste = resteAPoser(ressource, transports);
    if (reste.lte(0))
        return reste;
    // **Le reste est en heures TOTALES, la pastille porte les heures d'UNE unité** : c'est
    // ici que la division a lieu, et c'est la règle du métier — « 40 h / 5 = 8 h ». Sans
    // elle, une rotation à cinq camions engagerait cinq fois ce qui reste.
    const compte = unitesRetenues(unitesDeLaRotation, unitesRetenues(ressource.unites));
    const parUnite = reste.div(compte);
    return parUnite.gt(HEURES_MAXIMUM_PAR_PASTILLE) ? dec(HEURES_MAXIMUM_PAR_PASTILLE) : parUnite;
}
/**
 * `heures posées − heures chiffrées` : le dépassement, tel que la colonne H l'annonce.
 *
 * *« Tu peux annoncer colonne H posée le dépassement »* (métier, 13/08/2026, revenant sur
 * sa consigne initiale de n'y rien calculer — parce que les pastilles font désormais bouger
 * les heures).
 *
 * Zéro = ce qui est posé correspond au chiffrage. Positif = il y a plus de transport sur le
 * calendrier que le déboursé n'en porte, et c'est le moment où le conducteur décide : soit
 * il corrige une pastille — et les euros suivent —, soit il assume l'écart. **Il s'affiche,
 * il ne bloque pas** : le même parti pris que le reste à livrer et que le coût de l'arrondi.
 */
export function depassementDeTransport(ressource, transports) {
    // L'exact opposé du reste, et calculé par lui : deux soustractions écrites séparément
    // finiraient par ne pas se répondre, et l'écran afficherait « il reste 3 h » à côté de
    // « dépassement de 4 h ».
    return resteAPoser(ressource, transports).negated();
}
/**
 * Ce que les transports d'une ligne occupent : leur premier et leur dernier jour.
 *
 * Comme les livraisons : une pastille est un instant, il n'y a pas de durée à sommer. C'est
 * cette étendue qui entre dans le résumé du titre et dans la frise — un chantier dont le
 * dernier camion passe à J30 est occupé jusqu'à J30.
 */
export function etendueDesTransports(transports) {
    let debut = null;
    let fin = null;
    for (const t of transports) {
        if (debut === null || t.jourRelatif < debut)
            debut = t.jourRelatif;
        if (fin === null || t.jourRelatif > fin)
            fin = t.jourRelatif;
    }
    return { debut, fin };
}
/**
 * La question de la provenance se pose-t-elle sur cette LIGNE ?
 *
 * Sur ce qui se livre, et sur cela seulement — le prédicat des livraisons, retrouvé après
 * un détour d'une journée.
 *
 * *La 019 l'avait élargi aux lignes de transport, pour y porter le fournisseur et le mode
 * d'acheminement. Le métier l'a corrigé le 13/08/2026 en regardant l'écran : ces
 * informations appartiennent à la **pastille**, pas à la ligne. Un camion charge à la
 * carrière du groupe mardi et chez un fournisseur jeudi ; une réponse par camion aurait
 * obligé à créer une ligne par provenance — inventer du chiffrage pour décrire un
 * calendrier. Voir la migration 020.*
 *
 * La fonction subsiste comme point d'entrée unique — l'écran, la validation du serveur et la
 * lecture doivent porter sur exactement le même ensemble — même si elle ne fait plus que
 * déléguer. Le jour où un troisième cas apparaîtra, il s'ajoutera ici et nulle part ailleurs.
 */
export function provenanceApplicable(ressource) {
    return porteDesLivraisons(ressource);
}
/* ---------------------------------------------------------------------------
 * Étirer une barre
 * ------------------------------------------------------------------------- */
/**
 * De combien les heures changent quand la durée change.
 *
 * *Règle tranchée par le métier le 10/08/2026 : « le fait d'étirer une barre devra
 * recalculer la quantité d'heures et donc le déboursé ». Le nombre d'unités reste
 * constant ; allonger la barre allonge le travail.*
 *
 * **Le facteur porte sur la LIGNE depuis la migration 012**, et c'est ce qui change
 * tout : une ligne de 10 demi-journées portant 40 h, étirée à 20, passe à 80 h — et ses
 * voisines ne bougent pas. Appliqué à la tâche, il multipliait toutes ses lignes en
 * heures ensemble : rallonger la pelle rallongeait le chef de chantier avec elle, et le
 * conducteur n'avait aucun moyen de dire « la pelle reste deux jours de plus ».
 *
 * Rend `null` quand le facteur n'a pas de sens : durée d'origine nulle (rien à
 * multiplier) ou durée identique (rien à changer). L'appelant doit alors **ne rien
 * écrire** plutôt que de multiplier par un.
 */
export function facteurEtirement(demiJourneesAvant, demiJourneesApres) {
    if (demiJourneesAvant <= 0 || demiJourneesApres <= 0)
        return null;
    if (demiJourneesAvant === demiJourneesApres)
        return null;
    return dec(demiJourneesApres).div(dec(demiJourneesAvant));
}
/**
 * Ce que l'arrondi à la demi-journée coûte sur un chantier entier.
 *
 * Chiffré à la demande du métier — « il y a là beaucoup de coûts cachés qui
 * devraient apparaître ». Mesuré sur trois chantiers réels avant que les barres
 * n'existent : **+170 h, soit +4 704 €**.
 *
 * ## La maille a changé avec la migration 012, et le chiffre avec elle
 *
 * L'arrondi se comptait sur la **tâche** : une durée, un effectif, une multiplication.
 * Le 11/08/2026, cette maille avait été préférée à celle de la ressource — chiffrée
 * six fois plus cher — parce qu'elle suppose la mutualisation d'une demi-journée entre
 * les gens d'une même tâche, là où l'autre supposait l'inverse.
 *
 * Ce n'était pas un choix entre deux approximations : c'était l'absence de la donnée
 * qui aurait tranché. Depuis que chaque ligne porte SON nombre d'unités (011) et SA
 * durée (012), la question ne se pose plus — **le conducteur l'a dit ligne par ligne**,
 * et le total n'a plus à supposer quoi que ce soit. Deux ouvriers de 8 h qui partagent
 * deux demi-journées ne gaspillent rien ; une pelle qui reste un jour de plus que son
 * conducteur le dit désormais elle-même.
 *
 * L'arrondi reste **compté par tâche** pour `tachesArrondies` : c'est la maille que le
 * métier lit à l'écran, une ligne par poste. Seule la façon de l'obtenir a changé.
 */
export function coutDeLArrondi(racines, 
/**
 * Les barres posées, par numéro de poste. Vide = aucun planning.
 *
 * **Chaque LIGNE est comptée sur sa propre durée depuis la migration 012.** Avant,
 * toutes les ressources d'une tâche étaient comptées sur la durée de celle-ci : dès
 * qu'une pelle reste deux jours de plus que son conducteur, ce total devenait faux
 * pour les deux à la fois. `demiJourneesRetenues` tranche pour chacune — la durée
 * qu'elle pose, ou celle que ses heures impliquent quand rien n'est posé.
 */
phasesParPoste = new Map()) {
    let chiffrees = dec(0);
    let immobilisees = dec(0);
    let taches = 0;
    let arrondies = 0;
    let planifiees = 0;
    let chiffreesMat = dec(0);
    let immobiliseesMat = dec(0);
    let coutMat = dec(0);
    let machinesComptees = 0;
    let machinesDeclarables = 0;
    const descendre = (noeuds) => {
        for (const noeud of noeuds) {
            if (!noeud.retenu)
                continue;
            if (noeud.nature === "chapitre") {
                descendre(noeud.enfants);
                continue;
            }
            // Une tâche sans heures de main-d'œuvre ne s'étale pas dans le temps : elle
            // n'a rien à arrondir, et la compter gonflerait le dénominateur.
            if (noeud.heuresMO.lte(0))
                continue;
            taches++;
            const barres = phasesParPoste.get(noeud.numero) ?? [];
            const { parRang } = repartirLesBarres(barres);
            if (dureeDeLaTache(barres).demiJournees > 0)
                planifiees++;
            /**
             * La MAIN-D'ŒUVRE, ligne à ligne.
             *
             * Le périmètre est exactement celui de `heuresMO` — main-d'œuvre qui porte la
             * durée, transport exclu —, et par le même prédicat : la somme des heures
             * chiffrées d'ici doit être `heuresMO` au centime, sinon l'écart affiché serait
             * un artefact de périmètre plutôt qu'un coût d'arrondi.
             *
             * **Chaque ligne immobilise sa durée × son nombre d'unités.** Trois personnes sur
             * trois demi-journées, ce sont 36 h de présence, pas 12 : plus on met de monde
             * pour raccourcir, plus l'arrondi à la demi-journée coûte cher.
             */
            let immoDeLaTache = dec(0);
            for (const r of noeud.ressources) {
                if (r.statut === STATUT_RETIRE)
                    continue;
                if (!estEnHeuresDeMainDOeuvre(r) || !r.porteDuree)
                    continue;
                // Le transport ne s'arrondit pas : ses heures se posent au quart d'heure près sur
                // des pastilles, il n'y a aucune demi-journée entamée à lui faire payer.
                if (porteDesTransports(r))
                    continue;
                const demies = demiJourneesRetenues(r, parRang.get(r.rang));
                immoDeLaTache = immoDeLaTache.plus(heuresImmobilisees(demies, r.unites ?? 1));
                chiffrees = chiffrees.plus(r.quantiteReelle);
            }
            immobilisees = immobilisees.plus(immoDeLaTache);
            // L'arrondi se compte toujours par TÂCHE — c'est la maille que le métier lit à
            // l'écran, une ligne par poste. Ce qui a changé est la façon de l'obtenir : la
            // somme de ses lignes, et non un calcul sur sa durée globale.
            if (immoDeLaTache.minus(noeud.heuresMO).gt(dec(TOLERANCE_HEURE)))
                arrondies++;
            /**
             * Les MACHINES, chacune sur SA durée.
             *
             * Deux niveleuses sur quatre demi-journées immobilisent 32 h de niveleuse, pour
             * 28,75 h chiffrées : 3,25 h de trop, à 79 € l'heure. Le surcoût s'attribue à la
             * ligne, donc son prix est exact.
             *
             * **Seules les lignes où le conducteur a inscrit un nombre comptent.** C'est sa
             * règle, et c'est ce qui dispense de classer les 62 codes du référentiel : les
             * rotations de transport restent vides, donc hors du total. `machinesDeclarables`
             * dit combien de lignes attendent encore une réponse, pour qu'un total maigre ne
             * passe jamais pour un total complet.
             */
            for (const r of noeud.ressources) {
                if (!estMachineImmobilisee(r))
                    continue;
                if (porteDesTransports(r))
                    continue;
                machinesDeclarables++;
                if (r.unites === null)
                    continue;
                machinesComptees++;
                const occupeesMat = heuresImmobilisees(demiJourneesRetenues(r, parRang.get(r.rang)), r.unites);
                chiffreesMat = chiffreesMat.plus(r.quantiteReelle);
                immobiliseesMat = immobiliseesMat.plus(occupeesMat);
                coutMat = coutMat.plus(occupeesMat.minus(r.quantiteReelle).times(r.prixUnitaire));
            }
        }
    };
    descendre(racines);
    return {
        heuresChiffrees: chiffrees.toFixed(),
        heuresImmobilisees: immobilisees.toFixed(),
        heuresCachees: immobilisees.minus(chiffrees).toFixed(),
        taches,
        tachesArrondies: arrondies,
        tachesPlanifiees: planifiees,
        heuresChiffreesMateriel: chiffreesMat.toFixed(),
        heuresImmobiliseesMateriel: immobiliseesMat.toFixed(),
        heuresCacheesMateriel: immobiliseesMat.minus(chiffreesMat).toFixed(),
        coutCacheMateriel: coutMat.toFixed(),
        machinesComptees,
        machinesDeclarables,
    };
}
//# sourceMappingURL=planning.js.map